import { useEffect, useRef } from 'react'
import { useSelector } from 'react-redux'

import useDispatch from 'hooks/useDispatch'
import { selectCurrentMerchant } from 'selectors/merchant'
import { resetForm } from 'slices/orderFeedbackCollection'
import { selectIsFormDirty } from 'slices/orderFeedbackCollection/selectors'

export const useFeedbackCollectionFormInitialize = () => {
  const dispatch = useDispatch()
  const merchant = useSelector(selectCurrentMerchant)
  // Key the effect on the merchant *identity* — not the full object reference —
  // so a successful PATCH (which invalidates the merchant cache and produces a
  // new object reference for the same merchant) does NOT trigger resetForm and
  // overwrite the user's in-progress edits. The body is also idempotent (it
  // would set the same value on the same merchant); the id-keyed dep makes the
  // no-op explicit. A real change in merchant identity (e.g. switching accounts
  // in admin) re-initializes correctly. Copied from
  // scenes/Cms/Settings/PostOrderFeedback/hooks/usePostOrderFeedbackFormInitialize.
  const merchantId = merchant?.id
  const isMerchantLoaded = Boolean(merchantId)

  // AI-1282: this hook is also called from useWrittenFeedbackEnabled /
  // useWrittenFeedbackPersisted, whenever a DIFFERENT Settings sub-tab mounts
  // (Reply address, Notifications) — each call is its own independent effect,
  // so a dirty, unpublished edit made through THIS pane's own call can
  // otherwise be clobbered back to the last-persisted value the moment one of
  // those other calls fires on ITS OWN mount. A ref, not a dependency: reading
  // the LATEST dirty flag at effect-run time is enough to guard against that,
  // without adding a dependency that would re-run this same effect on every
  // dirty <-> clean flip — in particular right after Publish, before the
  // merchant record has necessarily caught up.
  const isFormDirtyRef = useRef(false)
  isFormDirtyRef.current = useSelector(selectIsFormDirty)

  useEffect(() => {
    // The id is also the loaded/not-loaded signal. `selectCurrentMerchant`
    // falls back to `{} as Merchant`, never null, so object truthiness cannot
    // tell the two apart: during the merchant GET the pane would seed from
    // `undefined ?? true` and paint the toggle ON for a merchant whose stored
    // value is false, then discard any click made in that window when the GET
    // lands and the effect re-runs.
    if (!merchantId) return
    if (isFormDirtyRef.current) return

    dispatch(
      resetForm({
        // `??`, not `||`: the merchant column defaults to TRUE, so an absent
        // or null field means "collecting" rather than "not collecting".
        // Until the nexus branch that adds `allow_written_feedback_collection`
        // deploys, the API response omits the field entirely.
        allowWrittenFeedbackCollection:
          merchant.allow_written_feedback_collection ?? true,
      })
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [merchantId, dispatch])

  // The pane disables its controls until this is true, so nobody can edit a
  // value that has not arrived yet.
  return isMerchantLoaded
}
